<template>
  <div class="hcl-tiles-tile-content-form-element">
    <component :is="componentName" :definitionField="definitionField" :dataField="dataField" :dataLookup="dataLookup"
      v-on:hcl-tiles-tile-change-height="onTilesTileChangeHeight" v-on:hcl-form-input-enter-key="onFormInputEnterKey" />
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'

import HclFormElement from '@/components/FormElement/FormElement.vue'
import HclFormWidget from '@/components/FormWidget/FormWidget.vue'

import { DefinitionTile, DefinitionFormField, DataFormField, DataFormLookup } from '@/components/types'

interface Props {
  /**
   * Definition as an JS object.
   */
  tile: DefinitionTile
}

const props = withDefaults(defineProps<Props>(), {
  tile: undefined
})

const definitionFields: DefinitionFormField[] = inject('definitionFields', [])
const dataFields: DataFormField[] = inject('dataFields', [])
const dataLookups: DataFormLookup[] = inject('dataLookups', [])

const definitionField = computed((): DefinitionFormField | undefined => {
  const match = definitionFields.filter((field) => {
    const fgroup = field.field.group && field.field.group.name
    return props.tile.entity.group === fgroup && props.tile.entity.name === field.field.name
  })

  return match ? match[0] : undefined
})

const dataField = computed((): DataFormField | any => {
  if (typeof dataFields === 'undefined') {
    throw new Error('HclTilesTileContentFormElement: dataFields is missing in data')
  }

  if (dataFields.length === 0) {
    throw new Error('HclTilesTileContentFormElement: dataFields has zero length')
  }

  let match: DataFormField[] = []

  if (props.tile.entity.group !== 'undefined') {
    match = dataFields.filter((field) => {
      return field.group === props.tile.entity.group && field.name === props.tile.entity.name
    })
  } else {
    match = dataFields.filter((field) => {
      return field.name === props.tile.entity.name
    })
  }

  if (match.length === 0)
    throw new Error(
      `HclTilesTileContentFormElement: dataField is missing in data: field ${props.tile.entity.name}`
    )

  return match[0]
})

const dataLookup = computed((): DataFormLookup | {} => {
  const match = definitionFields.filter((field: DefinitionFormField) => {
    const fgroup = field.field.group && field.field.group.name
    return props.tile.entity.group === fgroup && props.tile.entity.name === field.field.name
  })

  if (match.length === 0) return {}

  const matchl = dataLookups.filter((lookup: DataFormLookup) => {
    return lookup.name === match[0].field.lookup
  })

  return matchl ? matchl[0] : {}
})

const componentName = computed(() => {
  if (definitionField.value?.field.type === 'widget') return HclFormWidget

  return HclFormElement
})

const emit = defineEmits(['hcl-tiles-tile-change-height', 'hcl-form-input-enter-key'])

const onTilesTileChangeHeight = (event) => {
  emit('hcl-tiles-tile-change-height', { ...event, tileId: props.tile.id })
}

const onFormInputEnterKey = (event) => {
  emit('hcl-form-input-enter-key', event)
}
</script>
